import { equal } from '@/api/helpers';
import { Account } from '@/client/accounts';
import { Branch } from '@/client/branches';
import { SendTestEmailFormValues } from '@/client/campaigns';
import { CourseEntityEnrollEnum } from '@/client/courses';
import { Group } from '@/client/groups';
import { SecurityCultureSendSurveyFormValues } from '@/client/security-culture/types';
import { Subjects } from '@/client/users';
import { LoadingStatuses } from '@/common/constants';
import { TargetEntity, TargetFormValue } from '@/common/types/targets.type';
import { FormikMultiSelect } from '@/components/form';
import {
  useBranchesPartialRequest,
  useGroupsPartialRequest,
} from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { AppCalendarWithCustomTimepicker } from '@/ui/calendar/AppCalendarWithCustomTimepicker';
import { FlexContainer, FormContainer } from '@/ui/styled-ui';
import { formatTargetEntities } from '@/utils/targets';
import { Field, Form, Formik } from 'formik';
import moment from 'moment';
import { DropdownChangeEvent } from 'primereact/dropdown';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SendTestEmailModal } from '../campaigns';
import { securityCultureSelfServiceSchema } from './validations';

const CenteredForm = styled(Form)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const FieldsWrapper = styled.div`
  width: 100%;
  max-width: 450px;
`;

type SecurityCultureSendSurveyFormV2Props = {
  onSubmit: (
    data: SecurityCultureSendSurveyFormValues,
    resetForm: () => void,
    isTypeForm?: boolean,
  ) => void;
  onSendTestEmail: (
    data: SecurityCultureSendSurveyFormValues,
    test: SendTestEmailFormValues,
  ) => void;
  loading: LoadingStatuses;
  simpleTestEmail?: boolean;
  submitLabel?: string;
};

export const SecurityCultureSendSurveyFormV2: React.FC<
  SecurityCultureSendSurveyFormV2Props
> = ({ onSubmit, onSendTestEmail, loading, simpleTestEmail, submitLabel }) => {
  const { t } = useTranslation();
  const { canUseFeature } = useFeatureFlag();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const [testEmail, setTestEmail] = useState(false);
  const [initialValues] = useState<SecurityCultureSendSurveyFormValues>({
    target: undefined,
    targets: [],
    typeFormId: '',
    scheduledFor: undefined,
  });

  const groupedEntities = useRef<
    {
      label: string;
      items: TargetEntity[];
    }[]
  >([]);

  const { branches, isLoading: isBranchesLoading } = useBranchesPartialRequest({
    accountId: currentAccount?.id,
    filters: [equal('active', true)],
    sort: ['name,asc'],
    withDescendants: true,
    take: 20,
  });

  const { groups, isLoading: isGroupsLoading } = useGroupsPartialRequest({
    accountId: currentAccount?.id,
    filters: [equal('active', true)],
    sort: ['name,asc'],
  });

  useEffect(() => {
    if (currentAccount && !isBranchesLoading && !isGroupsLoading) {
      const entities = [];

      entities.push({
        label: t('account'),
        items: [currentAccount],
      });

      if (branches?.length) {
        entities.push({
          label: t('branches'),
          items: formatTargetEntities(
            branches,
            null,
            CourseEntityEnrollEnum.BRANCHES,
          ),
        });
      }

      if (groups?.length) {
        entities.push({
          label: t('groups'),
          items: formatTargetEntities(
            groups,
            null,
            CourseEntityEnrollEnum.GROUPS,
          ),
        });
      }

      groupedEntities.current = entities;
    }
  }, [isBranchesLoading, isGroupsLoading, branches, groups, currentAccount]);

  const checkIsOptionDisabled = (
    targets: TargetFormValue[],
    option: TargetFormValue,
  ) => {
    if (
      targets.find(
        (entity: TargetFormValue) =>
          entity.id === currentAccount?.id && option.id !== currentAccount?.id,
      ) ||
      targets
        .filter((target: TargetEntity) => !!target?.descendants)
        .find((target: TargetEntity) =>
          target.descendants?.find((x: any) => x.id === option.id),
        )
    )
      return true;
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={securityCultureSelfServiceSchema(t)}
      validateOnMount
      onSubmit={(data, { resetForm }) =>
        onSubmit(
          data,
          resetForm,
          canUseFeature(Subjects.SECURITY_CULTURE_TYPEFORM_INTEGRATION),
        )
      }
    >
      {({ setFieldValue, values, errors }) => {
        return (
          <FormContainer>
            <CenteredForm>
              <FieldsWrapper>
                <div className="field w-full">
                  <Field
                    id="targets"
                    name="targets"
                    dropdownIcon={
                      isBranchesLoading || isGroupsLoading
                        ? 'pi pi-spinner pi-spin'
                        : 'pi pi-chevron-down'
                    }
                    filter
                    label={t('generic.recipients')}
                    className="w-full white"
                    display="chip"
                    placeholder={t('generic.select')}
                    required
                    optionDisabled={(option: Account | Branch | Group) =>
                      checkIsOptionDisabled(values.targets || [], option)
                    }
                    onChange={(event: DropdownChangeEvent) => {
                      const accountSelected = event.value.find(
                        (item: any) => item.id === currentAccount?.id,
                      );

                      if (accountSelected) {
                        setFieldValue('targets', [currentAccount]);
                      } else {
                        setFieldValue('targets', event.value);
                      }
                    }}
                    dataKey="id"
                    optionLabel="name"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    component={FormikMultiSelect}
                    options={groupedEntities.current}
                    disabled={
                      isBranchesLoading ||
                      isGroupsLoading ||
                      loading === LoadingStatuses.LOADING
                    }
                    tooltip={t('security.culture.branches.tooltip')}
                  />
                </div>
                <div className="field w-full mb-4">
                  <label htmlFor="scheduledFor">
                    {t('security.culture.scheduleFor')}
                  </label>
                  <AppCalendarWithCustomTimepicker
                    id="scheduledFor"
                    name="scheduledFor"
                    className="w-full white-input"
                    minDate={moment.utc().toDate()}
                    value={values.scheduledFor}
                    showTime
                    stepMinute={15}
                    stepHour={1}
                    onChange={(value: Date | null) =>
                      setFieldValue('scheduledFor', value)
                    }
                    readOnlyInput
                    showButtonBar
                    placeholder={t('security.culture.scheduleFor.placeholder')}
                    disabled={loading === LoadingStatuses.LOADING}
                  />
                </div>
                <FlexContainer
                  justify="center"
                  wrap="wrap"
                  gap={14}
                  style={{ marginTop: 'var(--big-padding)' }}
                >
                  <AppButton
                    label={
                      submitLabel ?? t('security.culture.sendSurveyTarget')
                    }
                    isSubmit
                    isDisabled={!!Object.keys(errors).length}
                    state={loading}
                  />
                  <AppButton
                    label={
                      simpleTestEmail
                        ? t('security.culture.send.survey.send.test.myself')
                        : t('camapign.testEmail.send')
                    }
                    severity="secondary"
                    type="outlined"
                    onClick={() => {
                      if (simpleTestEmail) {
                        onSendTestEmail(values, {
                          emailType: 'custom',
                          email: currentUser?.email ?? '',
                          firstName: currentUser?.firstName ?? '',
                          lastName: currentUser?.lastName ?? '',
                        });
                      } else {
                        setTestEmail(true);
                      }
                    }}
                    state={loading}
                  />
                </FlexContainer>
              </FieldsWrapper>

              <SendTestEmailModal
                email={currentUser?.email as string}
                visible={testEmail}
                onSubmit={(test: SendTestEmailFormValues) => {
                  onSendTestEmail(values, test);
                  setTestEmail(false);
                }}
                onHide={() => setTestEmail(false)}
                extended={false}
                withName
                isSecurityCulture
              />
            </CenteredForm>
          </FormContainer>
        );
      }}
    </Formik>
  );
};
